/* eslint-disable @next/next/no-img-element */
"use client";

import { useState, useRef, useEffect } from "react";
import { useReCaptcha } from "next-recaptcha-v3";
import { Button, Container, Flex, Card } from "@radix-ui/themes";

import {
  Cross1Icon,
  PlayIcon,
  ReloadIcon,
  MagicWandIcon,
  StopIcon,
} from "@radix-ui/react-icons";

import { fireSwal } from "./utils/ui";
import {
  getStreamWithEqualSizeChunks,
  getStreamWithHeadersRemoved,
} from "./utils/stream";
import { SynthInput } from "./ui/components/SynthInput";
import { useGenerateTextMutation } from "./mutations";

let isAudioWorkletModuleAdded = false;
export default function Home() {
  const { executeRecaptcha, loaded, error: captchaError } = useReCaptcha();
  const generateTextMutation = useGenerateTextMutation();

  const [state, setState] = useState<
    "ready" | "streaming" | "playing" | "error"
  >("ready");
  const [text, setText] = useState(
    "Resemble’s AI voice generator lets you create realistic human–like voiceovers in seconds. "
  );

  const hasSkippedHeaderRef = useRef(false);
  const hasReadMetadataRef = useRef(false);
  const audioWorkletNodeRef = useRef<AudioWorkletNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);

  const audioContextRef = useRef<AudioContext | null>(null);
  const streamBufferRef = useRef<Uint8Array[]>([]);
  const controllerRef = useRef<AbortController | null>(null);
  const generateControllerRef = useRef<AbortController | null>(null);
  const streamEndedRef = useRef(false);
  const stopRequestedRef = useRef(false);
  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | undefined>(
    undefined
  );

  useEffect(() => {
    return () => {
      if (readerRef.current) {
        readerRef.current.cancel(); // Cancel the stream reading when unmounting
        audioContextRef.current?.close();
      }
    };
  }, []);

  const errorHandler = (error: Error, reset?: boolean) => {
    if (error.name === "AbortError") {
      return;
    }
    fireSwal(
      "Error!",
      error.message || "Something went wrong. Please try again."
    );

    if (reset && reset === true) {
      setState("ready");
    } else {
      setState("error");
    }
  };

  const handleSynthesis = async (text: string) => {
    if (captchaError || !loaded) {
      errorHandler(
        new Error(
          "Failed to load Captcha, please reload the page and try again."
        )
      );
      return;
    }

    // reset everything
    if (audioWorkletNodeRef.current && audioWorkletNodeRef.current.port) {
      audioWorkletNodeRef.current.port.postMessage({ type: "reset-processor" });
    }
    stopRequestedRef.current = false;
    hasSkippedHeaderRef.current = false;
    hasReadMetadataRef.current = false;
    streamBufferRef.current = [];

    // Captcha verification
    let token = "";
    const recaptchaPromise = executeRecaptcha?.("synthesis_submit");
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error("Captcha timeout after 10 seconds"));
      }, 10000);
    });

    try {
      const result = await Promise.race([recaptchaPromise, timeoutPromise]);
      token = result as string;
    } catch (error) {
      errorHandler(new Error("Captcha verification failed"));
      return;
    }

    // start the synthesis
    try {
      controllerRef.current = new AbortController();
      const res = await fetch("/api/resemble", {
        method: "POST",
        signal: controllerRef.current.signal,
        headers: {
          "Content-Type": "application/json",
        },
        cache: "no-store", // TODO: check if this is needed
        body: JSON.stringify({ query: text, token }),
      });
      if (!res.ok || !res.body) {
        throw new Error("Unable to generate audio!");
      }
      const transformedStream = getStreamWithEqualSizeChunks(
        getStreamWithHeadersRemoved(res.body!)
      );
      const reader = transformedStream.getReader();

      if (reader) {
        readerRef.current = reader;
        while (true) {
          const { done, value } = await readerRef.current.read();

          if (done) {
            streamEndedRef.current = true;
            audioWorkletNodeRef.current?.port.postMessage({
              type: "audio-end",
            });
            break;
          }
          if (
            value &&
            value.length > 0 &&
            audioContextRef.current &&
            gainNodeRef.current
          ) {
            let chunk = value;

            if (chunk.length > 0) {
              streamBufferRef.current.push(chunk);
              if (state !== "playing") {
                setState("playing");
                // prevents a popping noise when starting playback
                // https://alemangui.github.io/ramp-to-value
                gainNodeRef.current.gain.setTargetAtTime(
                  1,
                  audioContextRef.current.currentTime + 0.015,
                  0.015
                );
              }
              audioWorkletNodeRef.current?.port.postMessage({
                type: "streamed-chunk",
                chunk,
              });
            }
          }
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        errorHandler(error);
      }
    }
  };

  const cancel = async () => {
    if (generateTextMutation.isPending) {
      generateControllerRef.current?.abort();
      setState("ready");
      return;
    }

    gainNodeRef.current!.gain.setTargetAtTime(
      0,
      audioContextRef.current!.currentTime,
      0.015
    );
    if (readerRef.current) {
      await readerRef.current.cancel();
    } // If we have an ongoing reader, cancel it
    if (controllerRef.current) {
      controllerRef.current.abort();
    }

    // Stop all audio processing
    if (audioWorkletNodeRef.current) {
      audioWorkletNodeRef.current.port.postMessage({
        type: "stop-processor",
      });
    }

    // Suspend the audio context
    if (audioContextRef.current) {
      audioContextRef.current.suspend();
    }
    streamBufferRef.current = [];
    setState("ready");
  };

  const handleFormSubmit = async () => {
    // why can't we use MediaSource? because: https://github.com/w3c/media-source/issues/55
    setState("streaming");
    hasSkippedHeaderRef.current = false;

    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext ||
        window.webkitAudioContext)({
        sampleRate: 22050,
      });
    }

    const initAudioWorklet = async () => {
      try {
        if (audioContextRef.current) {
          if (audioContextRef.current.state === "suspended") {
            await audioContextRef.current.resume();
          }
          // https://developer.mozilla.org/en-US/docs/Web/API/AudioWorkletNode/port#examples
          if (!isAudioWorkletModuleAdded) {
            await audioContextRef.current.audioWorklet.addModule(
              "/audio-processor.js"
            );
            isAudioWorkletModuleAdded = true;
          }
          if (!audioWorkletNodeRef.current) {
            audioWorkletNodeRef.current = new AudioWorkletNode(
              audioContextRef.current,
              "audio-processor"
            );
            if (gainNodeRef.current) {
              gainNodeRef.current.disconnect();
              gainNodeRef.current = null;
            }
            gainNodeRef.current = new GainNode(audioContextRef.current);
            gainNodeRef.current.gain.value = 0;
            audioWorkletNodeRef.current.connect(gainNodeRef.current);
            gainNodeRef.current.connect(audioContextRef.current.destination);

            audioWorkletNodeRef.current.port.onmessage = (event) => {
              if (event.data.type === "playback-complete") {
                setState("ready");
                audioContextRef.current?.suspend();
              }
            };
          } else {
            if (gainNodeRef.current) {
              gainNodeRef.current.disconnect();
              gainNodeRef.current = null;
            }
            gainNodeRef.current = new GainNode(audioContextRef.current);
            gainNodeRef.current.gain.value = 0;
            audioWorkletNodeRef.current.connect(gainNodeRef.current);
            gainNodeRef.current.connect(audioContextRef.current.destination);
          }
        }
      } catch (error) {
        if (error instanceof Error) {
          errorHandler(error);
        }
      }
    };

    if (audioContextRef.current.state === "suspended") {
      await audioContextRef.current.resume();
      initAudioWorklet().then(() => {
        handleSynthesis(text);
      });
    } else {
      initAudioWorklet().then(() => {
        handleSynthesis(text);
      });
    }
  };

  const handleAutoGenerate = async () => {
    // Captcha verification
    setState("streaming");
    let token = "";
    const recaptchaPromise = executeRecaptcha?.("generate_text");
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error("Captcha timeout after 10 seconds"));
      }, 10000);
    });

    try {
      const result = await Promise.race([recaptchaPromise, timeoutPromise]);
      token = result as string;

      // generate the text
      generateControllerRef.current = new AbortController();
      generateTextMutation.mutate(
        { token, controller: generateControllerRef.current },
        {
          onSuccess(data) {
            setState("ready");
            setText(data.text);
          },
          onError(error) {
            errorHandler(error, true);
          },
        }
      );
    } catch (error) {
      errorHandler(new Error("Captcha verification failed"));
      return;
    }
  };

  return (
    <div id="embed-app">
      <Container id="embed-container">
        <Card className="p-2 md:p-4">
          <Flex asChild gap={"4"} direction={"column"} align={"center"}>
            <div id="synthesize-form" className="md:!gap-6">
              <SynthInput
                text={text}
                setText={setText}
                state={state}
                setState={setState}
              />

              <div>
                {state === "ready" && !generateTextMutation.isPending && (
                  <div className="flex gap-4 flex-wrap items-center justify-center">
                    <Button
                      highContrast
                      type="button"
                      onClick={handleFormSubmit}
                    >
                      Synthesize
                      <PlayIcon aria-hidden />
                    </Button>
                    <Button
                      className="autogenerate"
                      type="button"
                      onClick={handleAutoGenerate}
                    >
                      Autogenerate
                      <MagicWandIcon aria-hidden />
                    </Button>
                  </div>
                )}
                {state === "error" && (
                  <Button type="button" onClick={handleFormSubmit}>
                    Retry
                    <ReloadIcon aria-hidden />
                  </Button>
                )}
                {(state === "streaming" || generateTextMutation.isPending) && (
                  <Button type="button" onClick={cancel}>
                    <Flex gap={"2"} align={"center"}>
                      Cancel
                      <Cross1Icon aria-hidden />
                    </Flex>
                  </Button>
                )}
                {state === "playing" && (
                  <Button type="button" onClick={cancel}>
                    Stop
                    <StopIcon aria-hidden />
                  </Button>
                )}
              </div>
            </div>
          </Flex>
        </Card>
      </Container>
    </div>
  );
}
